import * as signalR from '@microsoft/signalr';
import { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';

let globalConnection: signalR.HubConnection | null = null;
let connectionUsers = 0;

export const useNotificationSignalR = (hubUrl: string) => {
  const [isConnected, setIsConnected] = useState(globalConnection?.state === signalR.HubConnectionState.Connected);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const prevHubUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevHubUrlRef.current && prevHubUrlRef.current !== hubUrl && globalConnection) {
      console.log('SignalR URL changed, recreating connection...');
      globalConnection.stop().catch(console.error);
      globalConnection = null;
      connectionUsers = 0;
    }

    prevHubUrlRef.current = hubUrl;
    connectionUsers++;

    const setupConnection = () => {
      if (globalConnection) {
        setIsConnected(globalConnection.state === signalR.HubConnectionState.Connected);

        globalConnection.on('ReceiveMessage', setLastMessage);

        return () => {
          globalConnection?.off('ReceiveMessage', setLastMessage);
          connectionUsers--;

          if (connectionUsers === 0) {
            globalConnection?.stop().catch(console.error);
            globalConnection = null;
          }
        };
      }

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl)
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Information)
        .build();

      globalConnection = connection;

      const onReconnecting = () => {
        console.warn('SignalR reconnecting...');
        setIsConnected(false);
      };

      const onReconnected = () => {
        console.log('SignalR reconnected');
        setIsConnected(true);
      };

      const onClose = () => {
        console.error('SignalR connection closed');
        setIsConnected(false);

        toast.error('Connection closed due to error. Try refreshing this page to restart the connection.', {
          autoClose: false,
          position: 'top-right'
        });
      };

      connection.on('ReceiveMessage', setLastMessage);
      connection.onreconnecting(onReconnecting);
      connection.onreconnected(onReconnected);
      connection.onclose(onClose);

      connection
        .start()
        .then(() => {
          console.log('SignalR connected');
          setIsConnected(true);
        })
        .catch((err) => {
          console.error('SignalR connection error:', err);
          setIsConnected(false);
        });

      return () => {
        connectionUsers--;
        connection.off('ReceiveMessage', setLastMessage);

        if (connectionUsers === 0) {
          connection.stop().catch(console.error);
          globalConnection = null;
        }
      };
    };

    return setupConnection();
  }, [hubUrl]);

  const sendMessage = (method: string, payload?: any) => {
    if (globalConnection?.state === signalR.HubConnectionState.Connected) {
      globalConnection.send(method, payload).catch((err) => {
        console.error('Error sending message:', err);
      });
    } else {
      console.warn('SignalR not connected, message not sent.');
    }
  };

  const subscribe = (methodName: string, callback: (...args: any[]) => void) => {
    globalConnection?.on(methodName, callback);
    return () => {
      globalConnection?.off(methodName, callback);
    };
  };

  const unsubscribe = (methodName: string, callback: (...args: any[]) => void) => {
    globalConnection?.off(methodName, callback);
  };

  return {
    isConnected,
    lastMessage,
    sendMessage,
    subscribe,
    unsubscribe
  };
};
